import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Star, ChevronLeft, Target, Sparkles, Loader2, MapPin, Clock, ArrowRight, Rocket, Heart, DollarSign, Briefcase, CheckCircle, AlertTriangle, Calendar, TrendingUp, TrendingDown } from 'lucide-react';
import { toast } from 'sonner';
import ReactMarkdown from 'react-markdown';
import { useAuth } from '@/components/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DECISION_ICONS = {
  project: { icon: Rocket, color: 'from-blue-500 to-cyan-500', label: 'Lancer un projet' },
  relationship: { icon: Heart, color: 'from-pink-500 to-rose-500', label: 'S\'engager / Rompre' },
  investment: { icon: DollarSign, color: 'from-green-500 to-emerald-500', label: 'Investir' },
  career: { icon: Briefcase, color: 'from-purple-500 to-indigo-500', label: 'Changer de carrière' }
};

const DecisionTypeCard = ({ type, info, selected, onSelect }) => {
  const { icon: Icon, color, label } = DECISION_ICONS[type] || {};
  
  return (
    <motion.button
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      onClick={() => onSelect(type)}
      className={`
        p-4 rounded-xl border transition-all text-left w-full
        ${selected 
          ? `bg-gradient-to-br ${color} border-white/30 text-gray-900` 
          : 'bg-white border-sky-200 hover:border-gray-300'}
      `}
      data-testid={`decision-type-${type}`}
    >
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${selected ? 'bg-white/20' : 'bg-gray-200'}`}>
          {Icon && <Icon className={`h-5 w-5 ${selected ? 'text-gray-900' : 'text-gray-700'}`} />}
        </div>
        <div>
          <p className={`font-semibold ${selected ? 'text-gray-900' : 'text-gray-900'}`}>{label}</p>
          <p className={`text-xs ${selected ? 'text-gray-900/70' : 'text-gray-600'}`}>{info?.description}</p>
        </div>
      </div>
    </motion.button>
  );
};

const ScoreGauge = ({ fluidity, friction, overall }) => {
  const overallColors = {
    favorable: { bg: 'bg-green-500', text: 'text-green-400', label: 'Favorable' },
    prudence: { bg: 'bg-orange-500', text: 'text-orange-400', label: 'Prudence' },
    nuancé: { bg: 'bg-blue-500', text: 'text-blue-400', label: 'Nuancé' }
  };
  
  const colors = overallColors[overall] || overallColors.nuancé;
  
  return (
    <div className="space-y-4">
      {/* Overall Badge */}
      <div className="flex justify-center">
        <span className={`px-4 py-2 rounded-full ${colors.bg}/20 ${colors.text} font-semibold text-lg border ${colors.bg}/40`}>
          {colors.label}
        </span>
      </div>
      
      {/* Score Bars */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-green-400 flex items-center gap-1"><TrendingUp className="h-4 w-4" /> Fluidité</span>
            <span className="text-gray-900 font-bold">{fluidity}%</span>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${fluidity}%` }}
              transition={{ duration: 1 }}
              className="h-full bg-gradient-to-r from-green-500 to-emerald-400 rounded-full"
            />
          </div>
        </div>
        
        <div>
          <div className="flex justify-between text-sm mb-1">
            <span className="text-orange-400 flex items-center gap-1"><TrendingDown className="h-4 w-4" /> Friction</span>
            <span className="text-gray-900 font-bold">{friction}%</span>
          </div>
          <div className="h-3 bg-gray-200 rounded-full overflow-hidden">
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${friction}%` }}
              transition={{ duration: 1, delay: 0.2 }}
              className="h-full bg-gradient-to-r from-orange-500 to-red-400 rounded-full"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

const FactorsList = ({ factors, type }) => {
  const isPositive = type === 'positive';
  
  return (
    <div className={`p-4 rounded-xl ${isPositive ? 'bg-green-500/10 border border-green-500/30' : 'bg-orange-500/10 border border-orange-500/30'}`}>
      <h4 className={`font-semibold mb-2 flex items-center gap-2 ${isPositive ? 'text-green-400' : 'text-orange-400'}`}>
        {isPositive ? <CheckCircle className="h-4 w-4" /> : <AlertTriangle className="h-4 w-4" />}
        {isPositive ? 'Facteurs Favorables' : 'Points d\'Attention'}
      </h4>
      <ul className="space-y-1">
        {factors?.length > 0 ? (
          factors.map((f, i) => (
            <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
              <span className={`mt-1.5 w-1.5 h-1.5 rounded-full shrink-0 ${isPositive ? 'bg-green-400' : 'bg-orange-400'}`} />
              {f}
            </li>
          ))
        ) : (
          <li className="text-sm text-gray-600 italic">Aucun facteur particulier</li>
        )}
      </ul>
    </div>
  );
};

const AstrozodiacDecisionPage = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [step, setStep] = useState(1);
  const [selectedType, setSelectedType] = useState(null);
  const [decisionTypes, setDecisionTypes] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    birthDate: '',
    birthTime: '',
    birthCity: 'Paris',
    birthCountry: 'FR'
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [interpretation, setInterpretation] = useState(null);
  const [loadingInterpretation, setLoadingInterpretation] = useState(false);
  const [profilePreFilled, setProfilePreFilled] = useState(false);

  // Auto-fill from user profile
  useEffect(() => {
    if (isAuthenticated && user && !profilePreFilled) {
      const newFormData = { ...formData };
      let hasPreFilled = false;
      
      if (user.first_name || user.name) {
        newFormData.firstName = user.first_name || user.name;
        hasPreFilled = true;
      }
      if (user.birth_date) {
        newFormData.birthDate = user.birth_date;
        hasPreFilled = true;
      }
      if (user.birth_time) {
        newFormData.birthTime = user.birth_time;
        hasPreFilled = true;
      }
      if (user.birth_city) {
        newFormData.birthCity = user.birth_city;
        hasPreFilled = true;
      }
      
      if (hasPreFilled) {
        setFormData(newFormData);
        setProfilePreFilled(true);
        toast.success('Données pré-remplies depuis votre profil !', {
          description: 'Modifiez si nécessaire'
        });
      }
    }
  }, [isAuthenticated, user, profilePreFilled]);
  
  // Fetch decision types on mount
  useEffect(() => {
    fetch(`${API_URL}/api/astrozodiac/decision/types`)
      .then(res => res.json())
      .then(data => {
        if (data.success) setDecisionTypes(data.types);
      })
      .catch(console.error);
  }, []);
  
  const handleAnalyze = async () => {
    if (!selectedType || !formData.firstName || !formData.birthDate || !formData.birthCity) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setLoading(true);
    setResult(null);
    setInterpretation(null);
    
    try {
      const response = await fetch(`${API_URL}/api/astrozodiac/decision/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision_type: selectedType,
          birth_date: formData.birthDate,
          birth_time: formData.birthTime || null,
          birth_city: formData.birthCity,
          birth_country: formData.birthCountry,
          first_name: formData.firstName
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setResult(data);
        setStep(3);
        toast.success('Analyse terminée !');
      } else {
        toast.error(data.detail || 'Erreur d\'analyse');
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erreur de connexion');
    } finally {
      setLoading(false);
    }
  };
  
  const getInterpretation = async () => {
    if (!result) return;
    
    setLoadingInterpretation(true);
    
    try {
      const response = await fetch(`${API_URL}/api/astrozodiac/interpret/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          decision_data: result,
          first_name: formData.firstName
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setInterpretation(data.interpretation);
        toast.success('Interprétation générée !');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'interprétation');
    } finally {
      setLoadingInterpretation(false);
    }
  };
  
  const analysis = result?.decision_analysis || {};
  const DecisionIcon = selectedType ? DECISION_ICONS[selectedType]?.icon : Target;
  
  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-cyan-50">
      {/* Starfield */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        {[...Array(40)].map((_, i) => (
          <div key={i} className="absolute w-1 h-1 bg-white rounded-full animate-pulse"
            style={{ left: `${Math.random() * 100}%`, top: `${Math.random() * 100}%`, opacity: Math.random() * 0.4 + 0.1, animationDelay: `${Math.random() * 3}s` }} />
        ))}
      </div>
      
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-sky-100">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-3">
          <Link to="/astrozodiac" className="flex items-center gap-2 text-sky-600 hover:text-sky-500" data-testid="back-btn">
            <ChevronLeft className="h-5 w-5" />
          </Link>
          <div className="flex items-center gap-2">
            <Target className="h-5 w-5 text-purple-400" />
            <span className="font-bold text-gray-900">Mode Décision</span>
          </div>
        </div>
      </header>
      
      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Step 1: Choose Decision Type */}
        {step === 1 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-lg mx-auto">
            <div className="text-center mb-8">
              <div className="inline-flex p-4 rounded-2xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 border border-purple-200 mb-4">
                <Target className="h-10 w-10 text-purple-400" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">Mode Décision</h1>
              <p className="text-gray-600">Quelle décision envisages-tu ?</p>
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              {Object.entries(DECISION_ICONS).map(([type, info]) => (
                <DecisionTypeCard
                  key={type}
                  type={type}
                  info={decisionTypes?.[type] || { description: info.label }}
                  selected={selectedType === type}
                  onSelect={setSelectedType}
                />
              ))}
            </div>
            
            <Button
              onClick={() => setStep(2)}
              disabled={!selectedType}
              className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-gray-900 font-semibold rounded-xl py-6"
              data-testid="next-step-btn"
            >
              Continuer <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </motion.div>
        )}
        
        {/* Step 2: Birth Data */}
        {step === 2 && (
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-md mx-auto">
            <div className="text-center mb-8">
              <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${DECISION_ICONS[selectedType]?.color || 'from-purple-500 to-pink-500'} mb-4`}>
                {DecisionIcon && <DecisionIcon className="h-10 w-10 text-gray-900" />}
              </div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">{DECISION_ICONS[selectedType]?.label}</h2>
              <p className="text-gray-600">Entre tes données de naissance</p>
            </div>
            
            <form className="space-y-5 bg-white p-6 rounded-2xl border border-sky-200">
              <div>
                <Label className="text-gray-700">Prénom *</Label>
                <Input type="text" value={formData.firstName} onChange={(e) => setFormData({ ...formData, firstName: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-firstname" />
              </div>
              
              <div>
                <Label className="text-gray-700">Date de naissance *</Label>
                <Input type="date" value={formData.birthDate} onChange={(e) => setFormData({ ...formData, birthDate: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-birthdate" />
              </div>
              
              <div>
                <Label className="text-gray-700 flex items-center gap-2"><Clock className="h-4 w-4" /> Heure (optionnel)</Label>
                <Input type="time" value={formData.birthTime} onChange={(e) => setFormData({ ...formData, birthTime: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-birthtime" />
              </div>
              
              <div>
                <Label className="text-gray-700 flex items-center gap-2"><MapPin className="h-4 w-4" /> Ville *</Label>
                <Input type="text" value={formData.birthCity} onChange={(e) => setFormData({ ...formData, birthCity: e.target.value })}
                  className="mt-1 bg-white border-gray-200 text-gray-900 rounded-xl" data-testid="input-city" />
              </div>
              
              <div className="flex gap-3">
                <Button type="button" variant="outline" onClick={() => setStep(1)}
                  className="flex-1 border-gray-200 text-gray-700 hover:bg-white rounded-xl">
                  Retour
                </Button>
                <Button type="button" onClick={handleAnalyze} disabled={loading}
                  className={`flex-1 bg-gradient-to-r ${DECISION_ICONS[selectedType]?.color || 'from-purple-500 to-pink-500'} text-gray-900 font-semibold rounded-xl`}
                  data-testid="analyze-btn">
                  {loading ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Analyse...</> : <><Sparkles className="mr-2 h-5 w-5" />Analyser</>}
                </Button>
              </div>
            </form>
          </motion.div>
        )}
        
        {/* Step 3: Results */}
        {step === 3 && result && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            {/* Header */}
            <div className="text-center">
              <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${DECISION_ICONS[selectedType]?.color || 'from-purple-500 to-pink-500'} mb-4`}>
                {DecisionIcon && <DecisionIcon className="h-10 w-10 text-gray-900" />}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {analysis.decision_info?.name || 'Analyse de Décision'}
              </h2>
              <p className="text-gray-600">pour {formData.firstName}</p>
            </div>
            
            {/* Score Gauge */}
            <div className="bg-white rounded-2xl border border-sky-200 p-6">
              <ScoreGauge 
                fluidity={analysis.fluidity_score || 50}
                friction={analysis.friction_score || 50}
                overall={analysis.overall}
              />
              
              {/* Timing */}
              <div className="mt-6 p-4 bg-gray-50 rounded-xl">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="h-5 w-5 text-sky-600" />
                  <span className="text-gray-900 font-semibold">{analysis.timing}</span>
                </div>
                <p className="text-sm text-gray-600">
                  Fenêtre : <span className="text-sky-600">{analysis.timing_window?.window_start}</span>
                  {' - '}{analysis.timing_window?.advice}
                </p>
              </div>
            </div>
            
            {/* Factors */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FactorsList factors={analysis.positive_factors} type="positive" />
              <FactorsList factors={analysis.negative_factors} type="negative" />
            </div>
            
            {/* Recommendations */}
            <div className="bg-white rounded-2xl border border-sky-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Target className="h-5 w-5 text-purple-400" />
                Recommandations
              </h3>
              <ul className="space-y-2">
                {analysis.recommendations?.map((rec, i) => (
                  <li key={i} className="flex items-start gap-2 text-gray-700">
                    <CheckCircle className="h-5 w-5 text-sky-600 shrink-0 mt-0.5" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
            
            {/* AI Interpretation */}
            <div className="bg-white rounded-2xl border border-sky-200 p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Sparkles className="h-5 w-5 text-sky-600" />
                Interprétation IA Approfondie
              </h3>
              
              {interpretation ? (
                <div className="prose prose-sm max-w-none">
                  <ReactMarkdown>{interpretation}</ReactMarkdown>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Button onClick={getInterpretation} disabled={loadingInterpretation}
                    className="bg-gradient-to-r from-sky-500 to-cyan-500 text-gray-900 rounded-xl"
                    data-testid="get-interpretation-btn">
                    {loadingInterpretation ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" />Analyse...</> : <><Sparkles className="mr-2 h-5 w-5" />Obtenir l'interprétation IA</>}
                  </Button>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="flex flex-wrap gap-4 justify-center">
              <Button variant="outline" onClick={() => { setStep(1); setResult(null); setInterpretation(null); setSelectedType(null); }}
                className="border-gray-200 text-gray-700 hover:bg-white rounded-xl" data-testid="new-decision-btn">
                Nouvelle décision
              </Button>
              <Button onClick={() => navigate('/astrozodiac/transits')}
                className="bg-gradient-to-r from-amber-500 to-yellow-500 text-black rounded-xl" data-testid="go-transits-btn">
                Voir mes transits <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default AstrozodiacDecisionPage;
